import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);

    this.#syncDrawerEmptyState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows.find((row) => Number(row.dataset.lineIndex) === line);

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    const cartItemRowToRemove = this.refs.cartItemRows.find((row) => Number(row.dataset.lineIndex) === line);

    if (!cartItemRowToRemove) return;

    // Get all keys to remove (entire group when applicable, including hidden rows)
    const keysToRemove = this.#getKeysToRemove(cartItemRowToRemove);
    const rowsToRemove = this.#getRowsToRemove(cartItemRowToRemove);

    // Build updates object: set quantity to 0 for items to remove
    const updates = {};
    keysToRemove.forEach((key) => {
      updates[key] = 0;
    });

    const remainingVisibleRows = this.refs.cartItemRows.filter((row) => !rowsToRemove.includes(row));
    const packageProtectionKey = this.querySelector('[data-package-protection-key]')?.getAttribute(
      'data-package-protection-key'
    );

    if (remainingVisibleRows.length === 0 && packageProtectionKey) {
      updates[packageProtectionKey] = 0;
    }

    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker('clear:user-action');
    this.#disableCartItems();

    const { cartTotal } = this.refs;
    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_update_url}`, fetchConfig('json', {
      body: JSON.stringify({ updates }),
    }))
      .then((response) => response.json())
      .then((updatedCart) => {
        resetShimmer(this);

        // If the cart is now empty, show empty state
        if (updatedCart.item_count === 0) {
          const template = document.getElementById('empty-cart-template');
          if (template instanceof HTMLTemplateElement) {
            const clone = document.importNode(template.content, true);
            startViewTransition(() => {
              this.replaceChildren(clone);
              this.#syncDrawerEmptyState(0);
            }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);
          }
        } else {
          // Animate removal of rows
          rowsToRemove.forEach((row) => {
            const remove = () => row.remove();
            if (prefersReducedMotion()) return remove();
            row.style.setProperty('--row-height', `${row.clientHeight}px`);
            row.classList.add('removing');
            onAnimationEnd(row, remove);
          });

          // Re-render the section after a delay to allow animation
          setTimeout(() => {
            sectionRenderer.renderSection(this.sectionId, { cache: false });
          }, 600);
        }

        this.dispatchEvent(
          new CartUpdateEvent(updatedCart, this.sectionId, {
            itemCount: updatedCart.item_count,
            source: 'cart-items-component',
            sections: updatedCart.sections,
          })
        );
      })
      .catch((error) => {
        console.error('Error removing group items:', error);
        sectionRenderer.renderSection(this.sectionId, { cache: false });
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Gets all rows that should be removed together with the given row.
   * @param {HTMLTableRowElement} cartItemRowToRemove - The row to remove.
   * @returns {HTMLTableRowElement[]} - Array of rows to remove.
   */
  #getRowsToRemove(cartItemRowToRemove) {
    const rowsToRemove = [cartItemRowToRemove];

    // Get all nested lines of the row to remove
    const nestedRows = this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key);
    rowsToRemove.push(...nestedRows);

    // Get all items with the same group ID
    const groupId = cartItemRowToRemove.dataset.groupId;
    if (groupId) {
      const groupRows = this.refs.cartItemRows.filter((row) => row.dataset.groupId === groupId && !rowsToRemove.includes(row));
      rowsToRemove.push(...groupRows);
    }

    return rowsToRemove;
  }

  /**
   * Gets all cart item keys that should be removed together with the given row.
   * Includes grouped hidden items that might not be rendered in the DOM.
   * @param {HTMLTableRowElement} cartItemRowToRemove - The row to remove.
   * @returns {string[]} - Array of cart item keys to remove.
   */
  #getKeysToRemove(cartItemRowToRemove) {
    const groupItemKeys = cartItemRowToRemove.dataset.groupItemKeys;
    if (groupItemKeys) {
      return groupItemKeys
        .split(',')
        .map((key) => key.trim())
        .filter(Boolean);
    }

    return [cartItemRowToRemove.dataset.key].filter(Boolean);
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;
    const lineItemRow = this.refs.cartItemRows.find((row) => Number(row.dataset.lineIndex) === line);

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const groupItemKeys = lineItemRow?.dataset.groupItemKeys
      ? lineItemRow.dataset.groupItemKeys
          .split(',')
          .map((key) => key.trim())
          .filter(Boolean)
      : [];

    const isGroupedQuantityUpdate = groupItemKeys.length > 0;

    const body = isGroupedQuantityUpdate
      ? JSON.stringify({
          updates: Object.fromEntries(groupItemKeys.map((key) => [key, quantity])),
          sections: Array.from(sectionsToUpdate).join(','),
          sections_url: window.location.pathname,
        })
      : JSON.stringify({
          line: line,
          quantity: quantity,
          sections: Array.from(sectionsToUpdate).join(','),
          sections_url: window.location.pathname,
        });

    cartTotal?.shimmer();

    fetch(isGroupedQuantityUpdate ? `${Theme.routes.cart_update_url}` : `${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], this.isDrawer ? 'hydration' : 'full');
        this.#syncDrawerEmptyState(newCartItemCount);

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
      this.#syncDrawerEmptyState(event.detail.itemCount);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Keeps the cart drawer empty state class in sync after live cart updates.
   * @param {number} [itemCount]
   */
  #syncDrawerEmptyState(itemCount) {
    if (!this.isDrawer) return;

    const drawer = this.closest('cart-drawer-component');
    const dialog = drawer?.querySelector('dialog');
    const isEmpty = typeof itemCount === 'number' ? itemCount === 0 : !this.querySelector('.cart-form');

    drawer?.classList.toggle('cart-drawer--empty', isEmpty);
    dialog?.classList.toggle('cart-drawer--empty', isEmpty);

    if (dialog) {
      dialog.setAttribute('aria-labelledby', isEmpty ? 'cart-drawer-heading-empty' : 'cart-drawer-heading');

      if (isEmpty) {
        dialog.setAttribute('cart-summary-sticky', 'false');
      }
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
